/**
 * Safety Repository
 * COMPLETE implementation for Inspections, Incidents, Drills, Risk Assessments
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Inspection, Incident, Drill, RiskAssessment } from '../../../types/database';

export class InspectionRepository extends BaseRepository<Inspection> {
  constructor() {
    super(db.inspections, 'inspections');
  }

  async findByType(type: string): Promise<Inspection[]> {
    return await this.table.where('inspectionType').equals(type).toArray();
  }

  async findByFrequency(frequency: Inspection['frequency']): Promise<Inspection[]> {
    return await this.table.where('frequency').equals(frequency).toArray();
  }

  async findByStatus(status: Inspection['status']): Promise<Inspection[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findOverdue(): Promise<Inspection[]> {
    const now = new Date();
    const pending = await this.table
      .where('status')
      .anyOf(['scheduled', 'in_progress'])
      .toArray();
    
    return pending.filter(insp =>
      new Date(insp.scheduledDate) < now
    );
  }

  async findUpcoming(days: number): Promise<Inspection[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table
      .where('scheduledDate')
      .between(now, futureDate, true, true)
      .filter(insp => insp.status === 'pending')
      .toArray();
  }
}

export class IncidentRepository extends BaseRepository<Incident> {
  constructor() {
    super(db.incidents, 'incidents');
  }

  async findByType(type: Incident['incidentType']): Promise<Incident[]> {
    return await this.table.where('incidentType').equals(type).toArray();
  }

  async findBySeverity(severity: Incident['severity']): Promise<Incident[]> {
    return await this.table.where('severity').equals(severity).toArray();
  }

  async findByStatus(status: Incident['status']): Promise<Incident[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<Incident[]> {
    return await this.table
      .where('occurredDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async getCritical(): Promise<Incident[]> {
    return await this.findBySeverity('critical');
  }

  async getOpen(): Promise<Incident[]> {
    return await this.table
      .where('status')
      .anyOf(['reported', 'investigating'])
      .toArray();
  }

  async search(searchTerm: string): Promise<Incident[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(inc =>
      inc.incidentNumber.toLowerCase().includes(term) ||
      inc.description.toLowerCase().includes(term) ||
      inc.location.toLowerCase().includes(term)
    ).toArray();
  }
}

export class DrillRepository extends BaseRepository<Drill> {
  constructor() {
    super(db.drills, 'drills');
  }

  async findByType(type: Drill['drillType']): Promise<Drill[]> {
    return await this.table.where('drillType').equals(type).toArray();
  }

  async findByStatus(status: Drill['status']): Promise<Drill[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findUpcoming(days: number): Promise<Drill[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table
      .where('scheduledDate')
      .between(now, futureDate, true, true)
      .filter(drill => drill.status === 'scheduled')
      .toArray();
  }

  async findOverdue(): Promise<Drill[]> {
    const now = new Date();
    return await this.table
      .where('status')
      .equals('scheduled')
      .filter(drill => new Date(drill.scheduledDate) < now)
      .toArray();
  }
}

export class RiskAssessmentRepository extends BaseRepository<RiskAssessment> {
  constructor() {
    super(db.riskAssessments, 'riskAssessments');
  }

  async findByType(type: string): Promise<RiskAssessment[]> {
    return await this.table.where('assessmentType').equals(type).toArray();
  }

  async findNeedingReview(): Promise<RiskAssessment[]> {
    const now = new Date();
    return await this.table.filter(ra =>
      ra.reviewDate !== undefined && new Date(ra.reviewDate) <= now
    ).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<RiskAssessment[]> {
    return await this.table
      .where('assessmentDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export const inspectionRepository = new InspectionRepository();
export const incidentRepository = new IncidentRepository();
export const drillRepository = new DrillRepository();
export const riskAssessmentRepository = new RiskAssessmentRepository();


